/**
 * parseMergeBranch — parses one `WHEN … THEN <action>` arm of a MERGE
 * statement. Returns a structured `WhenBranch` describing the match kind
 * (`matched` / `not-matched` / `not-matched-by-source`), an optional `AND`
 * condition span, and the action body (UPDATE / DELETE / INSERT / DO NOTHING /
 * unknown).
 */

import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import { trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import {
  IDENT_CONT,
  IDENT_START,
  consumeParenGroup,
  isWordBoundaryBefore,
  readColumnList,
  readWord,
  skipWsAndComments,
} from '../insert/insertParseHelpers';

export type WhenMatchKind = 'matched' | 'not-matched' | 'not-matched-by-source';

export type MergeActionKind = 'update' | 'delete' | 'insert' | 'do-nothing' | 'unknown';

export interface MergeAction {
  readonly kind: MergeActionKind;
  readonly bodyStart: number;
  readonly bodyEnd: number;
  readonly setStart: number | null;
  readonly setEnd: number | null;
  readonly insertColumns: readonly string[] | null;
  readonly valuesStart: number | null;
  readonly valuesEnd: number | null;
}

export interface WhenBranch {
  readonly matchKind: WhenMatchKind;
  readonly conditionText: string | null;
  readonly start: number;
  readonly end: number;
  readonly action: MergeAction;
}

export interface WhenHit {
  readonly start: number;
  readonly end: number;
}

export function findTopLevelWhen(
  source: string,
  from: number,
  end: number,
  ctx: SkipContext,
): WhenHit | null {
  return findTopLevelKeywordRaw(source, from, end, ctx, ['WHEN']);
}

export function parseBranches(
  source: string,
  from: number,
  end: number,
  ctx: SkipContext,
): WhenBranch[] {
  const out: WhenBranch[] = [];
  let cursor = from;
  while (cursor < end) {
    cursor = skipWsAndComments(source, cursor, end);
    if (cursor >= end) break;
    const w = readWord(source, cursor, end);
    if (!w || w.upper !== 'WHEN') break;
    const whenStart = w.start;
    const next = findTopLevelWhen(source, w.end, end, ctx);
    const whenEnd = next ? next.start : end;
    const branch = parseSingleBranch(source, whenStart, whenEnd, ctx);
    if (branch) out.push(branch);
    cursor = whenEnd;
  }
  return out;
}

function parseSingleBranch(
  source: string,
  start: number,
  end: number,
  ctx: SkipContext,
): WhenBranch | null {
  let i = skipWsAndComments(source, start, end);
  const whenWord = readWord(source, i, end);
  if (!whenWord || whenWord.upper !== 'WHEN') return null;
  i = skipWsAndComments(source, whenWord.end, end);

  let matchKind: WhenMatchKind = 'matched';
  const first = readWord(source, i, end);
  if (!first) return null;

  if (first.upper === 'NOT') {
    i = skipWsAndComments(source, first.end, end);
    const matched = readWord(source, i, end);
    if (!matched || matched.upper !== 'MATCHED') return null;
    i = skipWsAndComments(source, matched.end, end);
    matchKind = 'not-matched';
    const byProbe = readWord(source, i, end);
    if (byProbe && byProbe.upper === 'BY') {
      const ws = skipWsAndComments(source, byProbe.end, end);
      const target = readWord(source, ws, end);
      if (target && target.upper === 'SOURCE') {
        matchKind = 'not-matched-by-source';
        i = target.end;
      } else if (target && target.upper === 'TARGET') {
        i = target.end;
      }
    }
  } else if (first.upper === 'MATCHED') {
    i = first.end;
    matchKind = 'matched';
  } else {
    return null;
  }

  let conditionText: string | null = null;
  i = skipWsAndComments(source, i, end);
  const probe = readWord(source, i, end);
  if (probe && probe.upper === 'AND') {
    const condStart = probe.end;
    const thenHit = findTopLevelKeywordRaw(source, condStart, end, ctx, ['THEN']);
    if (!thenHit) return null;
    conditionText = source.slice(condStart, thenHit.start).trim();
    i = thenHit.end;
  } else {
    const thenHit = findTopLevelKeywordRaw(source, i, end, ctx, ['THEN']);
    if (!thenHit) return null;
    i = thenHit.end;
  }

  const action = parseAction(source, i, end, ctx);
  return { matchKind, conditionText, start, end, action };
}

function parseAction(
  source: string,
  from: number,
  end: number,
  ctx: SkipContext,
): MergeAction {
  const i = skipWsAndComments(source, from, end);
  const word = readWord(source, i, end);
  if (!word) return blankAction('unknown', from, end);

  if (word.upper === 'DELETE') return blankAction('delete', word.end, end);

  if (word.upper === 'DO') {
    const after = skipWsAndComments(source, word.end, end);
    const next = readWord(source, after, end);
    if (next && next.upper === 'NOTHING') return blankAction('do-nothing', next.end, end);
    return blankAction('unknown', word.end, end);
  }

  if (word.upper === 'UPDATE') {
    const cursor = skipWsAndComments(source, word.end, end);
    const setWord = readWord(source, cursor, end);
    if (setWord && setWord.upper === 'SET') {
      return {
        kind: 'update',
        bodyStart: word.end,
        bodyEnd: end,
        setStart: setWord.end,
        setEnd: end,
        insertColumns: null,
        valuesStart: null,
        valuesEnd: null,
      };
    }
    return blankAction('update', word.end, end);
  }

  if (word.upper === 'INSERT') {
    let cursor = skipWsAndComments(source, word.end, end);
    let insertColumns: readonly string[] | null = null;
    if (cursor < end && source[cursor] === '(') {
      const groupEnd = consumeParenGroup(source, cursor, end, ctx);
      insertColumns = readColumnList(source, cursor + 1, groupEnd - 1, ctx);
      cursor = skipWsAndComments(source, groupEnd, end);
    }
    let valuesStart: number | null = null;
    let valuesEnd: number | null = null;
    const valuesProbe = readWord(source, cursor, end);
    if (valuesProbe && valuesProbe.upper === 'VALUES') {
      const after = skipWsAndComments(source, valuesProbe.end, end);
      if (after < end && source[after] === '(') {
        const groupEnd = consumeParenGroup(source, after, end, ctx);
        valuesStart = after;
        valuesEnd = groupEnd;
      }
    }
    return {
      kind: 'insert',
      bodyStart: word.end,
      bodyEnd: end,
      setStart: null,
      setEnd: null,
      insertColumns,
      valuesStart,
      valuesEnd,
    };
  }

  return blankAction('unknown', from, end);
}

function blankAction(kind: MergeActionKind, bodyStart: number, bodyEnd: number): MergeAction {
  return {
    kind,
    bodyStart,
    bodyEnd,
    setStart: null,
    setEnd: null,
    insertColumns: null,
    valuesStart: null,
    valuesEnd: null,
  };
}

interface KeywordHit {
  readonly upper: string;
  readonly start: number;
  readonly end: number;
}

function findTopLevelKeywordRaw(
  source: string,
  from: number,
  end: number,
  ctx: SkipContext,
  targets: readonly string[],
): KeywordHit | null {
  let i = from;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (paren === 0 && IDENT_START.test(c) && isWordBoundaryBefore(source, i)) {
      let j = i + 1;
      while (j < end && IDENT_CONT.test(source[j])) j++;
      const word = source.slice(i, j).toUpperCase();
      if (targets.includes(word)) return { upper: word, start: i, end: j };
      i = j;
      continue;
    }
    i++;
  }
  return null;
}
